import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { errors, GetMetricStatisticInput, GetMetricStatisticOutput, Transfer } from '@smash-sdk/transfer/ejs/07-2020';
import moment from 'moment';
import { convertToGB } from '../../shared/helpers/helpers';
import { AsyncState } from '../../shared/models/async.interface';
import { RootState } from '../../store';

interface ChartData {
    xValues: (string | number)[];
    yValues: number[];
}
interface MetricState extends AsyncState {
    data?: ChartData;
}

export interface UsageState {
    transfers: MetricState;
    files: MetricState;
    bytesUploaded: MetricState;
    bytesDownloaded: MetricState;
    transferDownloads: MetricState;
    fileDownloads: MetricState;
}

export const loadUsages = createAsyncThunk(
    'usage/loadUsages',
    async ({ startDate, endDate, unit }
        : {
            startDate: GetMetricStatisticInput['startDate'],
            endDate: GetMetricStatisticInput['startDate'],
            unit: GetMetricStatisticInput['unit']
        }, thunkApi) => {

        thunkApi.dispatch(getTransfersCount({ startDate, endDate, unit }));
        thunkApi.dispatch(getFilesCount({ startDate, endDate, unit }));
        thunkApi.dispatch(getBytesUploadedCount({ startDate, endDate, unit }));
        thunkApi.dispatch(getBytesDownloadedCount({ startDate, endDate, unit }));
    }
)

function buildChartData({ statistic }: GetMetricStatisticOutput): ChartData {
    const xValues: string[] = [];
    const yValues: number[] = [];
    if (statistic.data?.length) {
        statistic.data.forEach(item => { xValues.push(moment.utc(item.date).format('MMM D')); yValues.push(item.value); })
    }
    return { xValues, yValues };
}

export const getTransfersCount = createAsyncThunk(
    'usage/getTransfersCount',
    async ({ startDate, endDate, unit }
        : {
            startDate: GetMetricStatisticInput['startDate'],
            endDate: GetMetricStatisticInput['startDate'],
            unit: GetMetricStatisticInput['unit']
        }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { statistic } = await transfer.getMetricStatistic({ metricName: 'transfers', statisticName: 'sum', startDate, endDate, unit });
            const { xValues, yValues } = buildChartData({ statistic });
            return { data: { xValues, yValues } };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const getFilesCount = createAsyncThunk(
    'usage/getFilesCount',
    async ({ startDate, endDate, unit }: { startDate: GetMetricStatisticInput['startDate'], endDate: GetMetricStatisticInput['startDate'], unit: GetMetricStatisticInput['unit'] }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { statistic } = await transfer.getMetricStatistic({ metricName: 'files', statisticName: 'sum', startDate, endDate, unit });
            const { xValues, yValues } = buildChartData({ statistic });
            return { data: { xValues, yValues } };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const getBytesUploadedCount = createAsyncThunk(
    'usage/getBytesUploadedCount',
    async ({ startDate, endDate, unit }: { startDate: GetMetricStatisticInput['startDate'], endDate: GetMetricStatisticInput['startDate'], unit: GetMetricStatisticInput['unit'] }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { statistic } = await transfer.getMetricStatistic({ metricName: 'bytesUploaded', statisticName: 'sum', startDate, endDate, unit });
            const { xValues, yValues } = buildChartData({ statistic });
            const yValuesTransformed = yValues.map(value => convertToGB(value));
            return { data: { xValues, yValues: yValuesTransformed } };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const getTransferDownloadsCount = createAsyncThunk(
    'usage/getTransferDownloadsCount',
    async ({ startDate, endDate, unit }: { startDate: GetMetricStatisticInput['startDate'], endDate: GetMetricStatisticInput['startDate'], unit: GetMetricStatisticInput['unit'] }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { statistic } = await transfer.getMetricStatistic({ metricName: 'transferDownloads', statisticName: 'sum', startDate, endDate, unit });
            const { xValues, yValues } = buildChartData({ statistic });
            return { data: { xValues, yValues } };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const getFileDownloadsCount = createAsyncThunk(
    'usage/getFileDownloadsCount',
    async ({ startDate, endDate, unit }: { startDate: GetMetricStatisticInput['startDate'], endDate: GetMetricStatisticInput['startDate'], unit: GetMetricStatisticInput['unit'] }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { statistic } = await transfer.getMetricStatistic({ metricName: 'fileDownloads', statisticName: 'sum', startDate, endDate, unit });
            const { xValues, yValues } = buildChartData({ statistic });
            return { data: { xValues, yValues } };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const getBytesDownloadedCount = createAsyncThunk(
    'usage/getBytesDownloadedCount',
    async ({ startDate, endDate, unit }: { startDate: GetMetricStatisticInput['startDate'], endDate: GetMetricStatisticInput['startDate'], unit: GetMetricStatisticInput['unit'] }, thunkApi) => {
        try {
            const transfer = new Transfer();
            const { statistic } = await transfer.getMetricStatistic({ metricName: 'bytesDownloaded', statisticName: 'sum', startDate, endDate, unit });
            const { xValues, yValues } = buildChartData({ statistic });
            const yValuesTransformed = yValues.map(value => convertToGB(value));
            return { data: { xValues, yValues: yValuesTransformed } };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)


export const initialState: UsageState = {
    transfers: {
        data: undefined,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    files: {
        data: undefined,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    bytesUploaded: {
        data: undefined,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    bytesDownloaded: {
        data: undefined,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    transferDownloads: {
        data: undefined,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    fileDownloads: {
        data: undefined,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
}

export const usageSlice = createSlice({
    name: 'usage',
    initialState,
    reducers: {
        reset: (state) => {
            state.transfers = initialState.transfers;
        }
    },
    extraReducers: (builder) => {
        builder
            // GetTransfersCount
            .addCase(getTransfersCount.pending, (state) => {
                state.transfers.isLoading = true;
                state.transfers.isError = false;
            })
            .addCase(getTransfersCount.fulfilled, (state, action) => {
                state.transfers.isLoading = false;
                state.transfers.isSuccess = true;
                state.transfers.data = action.payload.data;
            })
            .addCase(getTransfersCount.rejected, (state, action) => {
                state.transfers.isLoading = false;
                state.transfers.isError = true;
                state.transfers.data = undefined;
                state.transfers.error = action.payload as SerializedError;
            })

            // GetFilesCount
            .addCase(getFilesCount.pending, (state) => {
                state.files.isLoading = true;
                state.files.isError = false;
            })
            .addCase(getFilesCount.fulfilled, (state, action) => {
                state.files.isLoading = false;
                state.files.isSuccess = true;
                state.files.data = action.payload.data;
            })
            .addCase(getFilesCount.rejected, (state, action) => {
                state.files.isLoading = false;
                state.files.isError = true;
                state.files.data = undefined;
                state.files.error = action.payload as SerializedError;
            })

            // GetBytesUploadedCount
            .addCase(getBytesUploadedCount.pending, (state) => {
                state.bytesUploaded.isLoading = true;
                state.bytesUploaded.isError = false;
            })
            .addCase(getBytesUploadedCount.fulfilled, (state, action) => {
                state.bytesUploaded.isLoading = false;
                state.bytesUploaded.isSuccess = true;
                state.bytesUploaded.data = action.payload.data;
            })
            .addCase(getBytesUploadedCount.rejected, (state, action) => {
                state.bytesUploaded.isLoading = false;
                state.bytesUploaded.isError = true;
                state.bytesUploaded.error = action.payload as SerializedError;
                state.bytesUploaded.data = undefined;
            })

            // GetBytesDownloadedCount
            .addCase(getBytesDownloadedCount.pending, (state) => {
                state.bytesDownloaded.isLoading = true;
                state.bytesDownloaded.isError = false;
            })
            .addCase(getBytesDownloadedCount.fulfilled, (state, action) => {
                state.bytesDownloaded.isLoading = false;
                state.bytesDownloaded.isSuccess = true;
                state.bytesDownloaded.data = action.payload.data;
            })
            .addCase(getBytesDownloadedCount.rejected, (state, action) => {
                state.bytesDownloaded.isLoading = false;
                state.bytesDownloaded.isError = true;
                state.bytesDownloaded.error = action.payload as SerializedError;
                state.bytesDownloaded.data = undefined;
            })

            // getTransferDownloadsCount
            .addCase(getTransferDownloadsCount.pending, (state) => {
                state.transferDownloads.isLoading = true;
                state.transferDownloads.isError = false;
            })
            .addCase(getTransferDownloadsCount.fulfilled, (state, action) => {
                state.transferDownloads.isLoading = false;
                state.transferDownloads.isSuccess = true;
                state.transferDownloads.data = action.payload.data;
            })
            .addCase(getTransferDownloadsCount.rejected, (state, action) => {
                state.transferDownloads.isLoading = false;
                state.transferDownloads.isError = true;
                state.transferDownloads.error = action.payload as SerializedError;
                state.transferDownloads.data = undefined;
            })

            // GetFileDownloadsCount
            .addCase(getFileDownloadsCount.pending, (state) => {
                state.fileDownloads.isLoading = true;
                state.fileDownloads.isError = false;
            })
            .addCase(getFileDownloadsCount.fulfilled, (state, action) => {
                state.fileDownloads.isLoading = false;
                state.fileDownloads.isSuccess = true;
                state.fileDownloads.data = action.payload.data;
            })
            .addCase(getFileDownloadsCount.rejected, (state, action) => {
                state.fileDownloads.isLoading = false;
                state.fileDownloads.isError = true;
                state.fileDownloads.error = action.payload as SerializedError;
                state.fileDownloads.data = undefined;
            })
    }
});

export const { reset } = usageSlice.actions;
export const usageState = (state: RootState) => state.usage;

export default usageSlice.reducer;